import { supabase } from './supabase';
import { fetchAllAmiPoints } from './amiFetch';
import type {
  AdminUserRow,
  AdminProjectRow,
  AuditAction,
  AnalyticsSummary,
  CompletionValidationSnapshot,
  UtilityRequestRow,
  UtilityRequestStatus,
  UtilityDemandGroup,
} from '../types/admin';
import type { Project, Equipment, AMIDataPoint } from '../types/project';
import type { AmiStatus, UtilityRow } from '../types/utility';
import { invalidateUtilityDetail } from '../hooks/useUtility';

export interface FullProjectData {
  project: Project;
  equipment: Equipment[];
  amiData: AMIDataPoint[];
}

export async function fetchFullAdminProject(projectId: string): Promise<FullProjectData> {
  const { data: projectData, error: projectError } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();
  if (projectError) throw projectError;

  const { data: equipmentData, error: equipmentError } = await supabase
    .from('equipment')
    .select('*')
    .eq('project_id', projectId)
    .order('sort_order');
  if (equipmentError) throw equipmentError;

  const all = await fetchAllAmiPoints(supabase, projectId);

  return {
    project: projectData as Project,
    equipment: (equipmentData ?? []) as Equipment[],
    amiData: all,
  };
}

// ─── User management ─────────────────────────────────────────────────────────

export async function fetchAdminUsers(): Promise<AdminUserRow[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, email, full_name, role, created_at, last_active_at, deleted_at')
    .order('created_at', { ascending: false });

  if (error) throw error;
  const users = data ?? [];

  // Fetch project counts for all users in one query
  const userIds = users.map(u => u.id);
  const countMap: Record<string, { created: number; completed: number }> = {};
  if (userIds.length > 0) {
    const { data: projects } = await supabase
      .from('projects')
      .select('user_id, status')
      .in('user_id', userIds);
    if (projects) {
      for (const p of projects) {
        if (!countMap[p.user_id]) countMap[p.user_id] = { created: 0, completed: 0 };
        countMap[p.user_id].created += 1;
        if (p.status === 'completed') countMap[p.user_id].completed += 1;
      }
    }
  }

  // is_banned is not a user_profiles column — ban state lives on
  // auth.users.banned_until, which is only reachable with the service role.
  // The netlify/functions/admin-users endpoint derives it there; from the
  // client it is reported as not-banned.
  return users.map(u => ({
    ...u,
    is_banned: false,
    company_name: null,
    projects_created: countMap[u.id]?.created ?? 0,
    projects_completed: countMap[u.id]?.completed ?? 0,
  }));
}

export async function setUserRole(
  targetUserId: string,
  role: 'user' | 'admin'
): Promise<void> {
  const { error } = await supabase
    .from('user_profiles')
    .update({ role })
    .eq('id', targetUserId);
  if (error) throw error;
}


// ─── Audit trail ─────────────────────────────────────────────────────────────

export async function logAdminAction(
  adminUserId: string,
  action: AuditAction,
  targetUserId: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  const { error } = await supabase.from('admin_audit_events').insert({
    admin_user_id: adminUserId,
    action,
    target_user_id: targetUserId,
    metadata: metadata ?? null,
  });
  if (error) throw error;
}

// ─── Project visibility ───────────────────────────────────────────────────────

export async function fetchAdminProjects(filters?: {
  userId?: string;
  status?: string;
  from?: string;
  to?: string;
}): Promise<AdminProjectRow[]> {
  let query = supabase
    .from('projects')
    .select(
      `id, user_id, project_name, status, created_at, updated_at,
       completed_at, pass_fail, completion_validation,
       service_rating_amps, max_recorded_demand_kw,
       ami_measurement_period, ami_data_quality_notes,
       customer_name, ahj_jurisdiction,
       report_method, dwelling_floor_area_sqft`
    )
    .order('updated_at', { ascending: false });

  if (filters?.userId) query = query.eq('user_id', filters.userId);
  if (filters?.status) query = query.eq('status', filters.status);
  if (filters?.from) query = query.gte('created_at', filters.from);
  if (filters?.to) query = query.lte('created_at', filters.to);

  const { data, error } = await query;
  if (error) throw error;

  const rows = data ?? [];

  // Fetch emails for all unique user_ids in a single query
  const userIds = [...new Set(rows.map(p => p.user_id).filter(Boolean))];
  let emailMap: Record<string, string> = {};
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, email')
      .in('id', userIds);
    if (profiles) {
      emailMap = Object.fromEntries(profiles.map(u => [u.id, u.email]));
    }
  }

  return rows.map(p => ({
    ...p,
    user_email: emailMap[p.user_id] ?? null,
    adjusted_demand_kva: null,
  }));
}

// ─── Analytics aggregation ───────────────────────────────────────────────────

/**
 * Computes the full analytics summary from a list of project rows.
 * Pure function — safe to call in tests without a DB.
 */
export function computeAnalyticsSummary(
  projects: AdminProjectRow[]
): AnalyticsSummary {
  const now = Date.now();
  const msDay = 86_400_000;

  const since = (days: number) => now - days * msDay;

  const completed = projects.filter(p => p.status === 'completed');
  const passCount = completed.filter(p => p.pass_fail === 'PASS').length;
  const failCount = completed.filter(p => p.pass_fail === 'FAIL').length;

  const createdAfter = (days: number) =>
    projects.filter(p => new Date(p.created_at).getTime() >= since(days)).length;

  const completedAfter = (days: number) =>
    completed.filter(
      p => p.completed_at != null && new Date(p.completed_at).getTime() >= since(days)
    ).length;

  const warningAcknowledgedCount = completed.filter(
    p => p.completion_validation?.warningAcknowledged === true
  ).length;

  // Aggregate per-rule IDs from completion_validation snapshots into ranked
  // frequency lists (most common blocker/warning first). Legacy snapshots without
  // blockerIds/warningIds contribute nothing and are skipped.
  const tally = (pick: (cv: CompletionValidationSnapshot) => string[] | undefined) => {
    const counts = new Map<string, number>();
    for (const p of projects) {
      const cv = p.completion_validation;
      if (!cv) continue;
      for (const id of pick(cv) ?? []) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    return Array.from(counts, ([id, count]) => ({ id, count })).sort(
      (a, b) => b.count - a.count || a.id.localeCompare(b.id)
    );
  };

  const topBlockers = tally(cv => cv.blockerIds);
  const topWarnings = tally(cv => cv.warningIds);

  // Report basis per project. The stored basis wins; a project that has
  // filled a dwelling profile without a stored basis is on the traditional
  // path in practice; measured peak with no basis is measured in practice.
  const basisOf = (p: AdminProjectRow): 'measured' | 'traditional' | 'unset' => {
    if (p.report_method === 'traditional' || p.report_method === 'measured') return p.report_method;
    if (p.dwelling_floor_area_sqft != null && p.dwelling_floor_area_sqft > 0) return 'traditional';
    if (p.max_recorded_demand_kw != null && p.max_recorded_demand_kw > 0) return 'measured';
    return 'unset';
  };
  const methodMix = {
    measured: projects.filter(p => basisOf(p) === 'measured').length,
    traditional: projects.filter(p => basisOf(p) === 'traditional').length,
    unset: projects.filter(p => basisOf(p) === 'unset').length,
    completedMeasured: completed.filter(p => basisOf(p) === 'measured').length,
    completedTraditional: completed.filter(p => basisOf(p) === 'traditional').length,
  };

  return {
    totalProjects: projects.length,
    completedProjects: completed.length,
    passCount,
    failCount,
    completionRate:
      projects.length > 0
        ? Math.round((completed.length / projects.length) * 100)
        : 0,
    projectsCreated7d: createdAfter(7),
    projectsCreated30d: createdAfter(30),
    projectsCreated90d: createdAfter(90),
    projectsCompleted7d: completedAfter(7),
    projectsCompleted30d: completedAfter(30),
    projectsCompleted90d: completedAfter(90),
    topBlockers,
    topWarnings,
    warningAcknowledgedCount,
    methodMix,
  };
}

export async function fetchAnalyticsSummary(): Promise<AnalyticsSummary> {
  const projects = await fetchAdminProjects();
  return computeAnalyticsSummary(projects);
}


// ─── Utility demand ──────────────────────────────────────────────────────────

export async function fetchUtilityRequests(): Promise<UtilityRequestRow[]> {
  const { data, error } = await supabase
    .from('utility_requests')
    .select(
      `id, user_id, project_id, utility_id, utility_name, is_unlisted,
       service_city, service_state, service_zip, annual_jobs,
       has_contact, contact_note, status, created_at`
    )
    .order('created_at', { ascending: false });
  if (error) throw error;

  const rows = data ?? [];

  // Fetch emails for all unique user_ids in a single query
  const userIds = [...new Set(rows.map(r => r.user_id).filter(Boolean))];
  let emailMap: Record<string, string> = {};
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, email')
      .in('id', userIds);
    if (profiles) {
      emailMap = Object.fromEntries(profiles.map(u => [u.id, u.email]));
    }
  }

  return rows.map(r => ({ ...r, user_email: emailMap[r.user_id] ?? null }));
}

/**
 * Groups requests by utility name, case-insensitively, sorted by request count
 * descending. Pure function — safe to call in tests without a DB.
 */
export function groupUtilityRequests(rows: UtilityRequestRow[]): UtilityDemandGroup[] {
  const groups = new Map<string, UtilityRequestRow[]>();
  for (const row of rows) {
    const key = row.utility_name.trim().toLowerCase();
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }

  return [...groups.entries()]
    .map(([key, requests]) => {
      // Rows arrive newest-first, so the head carries the current spelling,
      // status and timestamp for the group.
      const newest = requests[0];
      return {
        key,
        utilityName: newest.utility_name.trim(),
        requestCount: requests.length,
        contractorCount: new Set(requests.map(r => r.user_id)).size,
        totalAnnualJobs: requests.reduce((sum, r) => sum + (r.annual_jobs ?? 0), 0),
        contactsOffered: requests.filter(r => r.has_contact).length,
        latestRequestAt: newest.created_at,
        isUnlisted: requests.some(r => r.is_unlisted),
        status: newest.status,
        requests,
      };
    })
    .sort((a, b) => b.requestCount - a.requestCount || a.utilityName.localeCompare(b.utilityName));
}

/** Applies a status to every request row for one utility. */
export async function setUtilityRequestStatus(
  requestIds: string[],
  status: UtilityRequestStatus
): Promise<void> {
  if (requestIds.length === 0) return;
  const { error } = await supabase
    .from('utility_requests')
    .update({ status })
    .in('id', requestIds);
  if (error) throw error;
}

// ─── Utilities (reference data) ──────────────────────────────────────────────
// The utilities table is admin-writable under RLS ("Admins can manage
// utilities"), so the Admin Utilities tab writes it directly. Reads are paged:
// the list is national and PostgREST caps one response at 1,000 rows.

const UTILITY_PAGE = 1000;

export async function fetchAdminUtilities(): Promise<UtilityRow[]> {
  const all: UtilityRow[] = [];
  for (let from = 0; ; from += UTILITY_PAGE) {
    const { data, error } = await supabase
      .from('utilities')
      .select('*')
      .order('state', { ascending: true })
      .order('name', { ascending: true })
      .range(from, from + UTILITY_PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as UtilityRow[];
    all.push(...rows);
    if (rows.length < UTILITY_PAGE) break;
  }
  return all;
}

/** The columns an admin may change from the Utilities tab. */
export type UtilityPatch = Partial<
  Pick<
    UtilityRow,
    | 'name'
    | 'state'
    | 'ami_status'
    | 'ami_contact_email'
    | 'ami_notes'
    | 'notes'
    | 'is_active'
    | 'portal_url'
    | 'download_steps'
    | 'export_format'
    | 'history_months'
    | 'rollout_note'
    | 'instructions_verified'
    | 'instructions_verified_at'
    | 'instructions_source_url'
    | 'instructions_feedback'
    | 'request_form_template'
  >
>;

export async function updateUtility(id: string, patch: UtilityPatch): Promise<UtilityRow> {
  const { data, error } = await supabase
    .from('utilities')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  invalidateUtilityDetail(id);
  return data as UtilityRow;
}

export async function createUtility(input: {
  name: string;
  state: string;
  ami_status: AmiStatus;
}): Promise<UtilityRow> {
  const { data, error } = await supabase
    .from('utilities')
    .insert({ name: input.name, state: input.state, ami_status: input.ami_status, ami_contact_email: '' })
    .select('*')
    .single();
  if (error) throw error;
  return data as UtilityRow;
}
